// © Copyright 2026 Emman Consulting SAS
// ─── TRADING SYSTEM FUNCTIONS ────────────────────────────────────────────
import DOMPurify from 'dompurify';
import { GROUPS, PANINI, FWC, CC_PLAYERS, FLAG_EMOJIS, sk } from '../data/albumData.js';

const APP_URL = import.meta.env.VITE_APP_URL || window.location.origin;

// Generate text for sharing missing stickers list
export function generateMissingListText(col) {
const missing = {};
PANINI.forEach(s=>{
if(!col[s.code]?.owned){
const prefix = "PANINI";
if(!missing[prefix]) missing[prefix] = [];
missing[prefix].push(s.code);
}
});
FWC.forEach(s=>{
if(!col[s.code]?.owned){
const match = s.code.match(/FWC\s*(\d+)/);
if(match){
const prefix = "FWC";
if(!missing[prefix]) missing[prefix] = [];
missing[prefix].push(parseInt(match[1]));
}
}
});
CC_PLAYERS.forEach(s=>{
if(!col[s.code]?.owned){
const prefix = "CC";
if(!missing[prefix]) missing[prefix] = [];
missing[prefix].push(s.code);
}
});
GROUPS.forEach(g=>{
g.teams.forEach(tc=>{
for(let i=1;i<=20;i++){
const code = sk(tc,i);
if(!col[code]?.owned){
if(!missing[tc]) missing[tc] = [];
missing[tc].push(i);
}
}
});
});
const albumOrder = ["PANINI","FWC","CC"];
GROUPS.forEach(g=>{ g.teams.forEach(tc=>albumOrder.push(tc)); });
const sortedGroups = Object.keys(missing).sort((a,b)=>albumOrder.indexOf(a)-albumOrder.indexOf(b));
const lines = sortedGroups.map(prefix=>{
const numbers = missing[prefix].sort((a,b)=>a-b);
const formatted = numbers.map(n=>typeof n==="string"?n:n.toString().padStart(2,"0")).join(", ");
return `${FLAG_EMOJIS[prefix] || ''} ${prefix}: ${formatted}`;
});
return DOMPurify.sanitize("Hola, Estas son las láminas que me faltan:\n\nRegístrate en la app: "+APP_URL+"/ para intercambiar!\n\n"+lines.join("\n"));
}

// Generate text for sharing duplicates list
export function generateDuplicatesListText(col) {
const dupes = {};
PANINI.forEach(s=>{
if(col[s.code]?.quantity>0){
const prefix = "PANINI";
if(!dupes[prefix]) dupes[prefix] = [];
dupes[prefix].push(s.code);
}
});
FWC.forEach(s=>{
if(col[s.code]?.quantity>0){
const match = s.code.match(/FWC\s*(\d+)/);
if(match){
const prefix = "FWC";
if(!dupes[prefix]) dupes[prefix] = [];
dupes[prefix].push(parseInt(match[1]));
}
}
});
CC_PLAYERS.forEach(s=>{
if(col[s.code]?.quantity>0){
const prefix = "CC";
if(!dupes[prefix]) dupes[prefix] = [];
dupes[prefix].push(s.code);
}
});
GROUPS.forEach(g=>{
g.teams.forEach(tc=>{
for(let i=1;i<=20;i++){
const code = sk(tc,i);
if(col[code]?.quantity>0){
if(!dupes[tc]) dupes[tc] = [];
dupes[tc].push(i);
}
}
});
});
const albumOrder = ["PANINI","FWC","CC"];
GROUPS.forEach(g=>{ g.teams.forEach(tc=>albumOrder.push(tc)); });
const sortedGroups = Object.keys(dupes).sort((a,b)=>albumOrder.indexOf(a)-albumOrder.indexOf(b));
const lines = sortedGroups.map(prefix=>{
const numbers = dupes[prefix].sort((a,b)=>a-b);
const formatted = numbers.map(n=>typeof n==="string"?n:n.toString().padStart(2,"0")).join(", ");
return `${FLAG_EMOJIS[prefix] || ''} ${prefix}: ${formatted}`;
});
return DOMPurify.sanitize("Hola, Estas son mis repetidas disponibles:\n\nRegístrate en "+APP_URL+"/ para intercambiar!\n\n"+lines.join("\n"));
}

// Parse friend's missing list from text
export function parseMissingList(text) {
const result = {};
const lines = text.split("\n");
lines.forEach(line=>{
const trimmed = line.trim();
// Handle both emoji-prefixed and non-emoji format: "🇦🇷 ARG: 01, 02, 03" or "ARG: 01, 02, 03"
const match = trimmed.match(/(?:[\p{Emoji_Presentation}💎⭐🏆👑🏴\u{1F3F4}]+\s+)?([A-Z]{2,3}):\s*([\d\s,]+)$/u);
if(match){
const prefix = match[1];
const numbers = match[2].split(",").map(n=>parseInt(n.trim())).filter(n=>!isNaN(n));
if(numbers.length>0){ result[prefix] = numbers; }
}
});
return result;
}

// Parse friend's duplicates list
export function parseDuplicatesList(text) {
const result = {};
const lines = text.split("\n");
lines.forEach(line=>{
const trimmed = line.trim();
// Handle both emoji-prefixed and non-emoji format: "🇦🇷 ARG: 01, 02, 03" or "ARG: 01, 02, 03"
const match = trimmed.match(/(?:[\p{Emoji_Presentation}💎⭐🏆👑🏴\u{1F3F4}]+\s+)?([A-Z]{2,3}):\s*([\d\s,]+)$/u);
if(match){
const prefix = match[1];
const numbers = match[2].split(",").map(n=>parseInt(n.trim())).filter(n=>!isNaN(n));
if(numbers.length>0){ result[prefix] = numbers; }
}
});
return result;
}

// Find matches between friend's missing list and my duplicates
export function findMatches(friendList, myInventory) {
const matches = {};
const friendMissing = {};
Object.keys(friendList).forEach(prefix=>{
friendList[prefix].forEach(num=>{ friendMissing[`${prefix}-${num}`] = true; });
});
Object.keys(myInventory).forEach(code=>{
const st = myInventory[code];
if(st.owned && st.quantity>=1){
let prefix, num;
if(code.startsWith("FWC ")){
prefix = "FWC";
const match = code.match(/FWC\s*(\d+)/);
if(match) num = parseInt(match[1]);
}else if(code.startsWith("CC ")){
prefix = "CC"; num = code;
}else{
const parts = code.split(" ");
if(parts.length>=2){ prefix = parts[0]; num = parseInt(parts[1]); }
}
if(prefix && num && friendMissing[`${prefix}-${num}`]){
if(!matches[prefix]) matches[prefix] = [];
matches[prefix].push(num);
}
}
});
return matches;
}

// Find matches between friend's duplicates and my missing stickers
export function findMissingMatches(friendDupes, myCollection) {
const matches = {};
const friendDuplicates = {};
Object.keys(friendDupes).forEach(prefix=>{
friendDupes[prefix].forEach(num=>{ friendDuplicates[`${prefix}-${num}`] = true; });
});
Object.keys(myCollection).forEach(code=>{
const st = myCollection[code];
if(!st.owned){
let prefix, num;
if(code.startsWith("FWC ")){
prefix = "FWC";
const match = code.match(/FWC\s*(\d+)/);
if(match) num = parseInt(match[1]);
}else if(code.startsWith("CC ")){
prefix = "CC"; num = code;
}else{
const parts = code.split(" ");
if(parts.length>=2){ prefix = parts[0]; num = parseInt(parts[1]); }
}
if(prefix && num && friendDuplicates[`${prefix}-${num}`]){
if(!matches[prefix]) matches[prefix] = [];
matches[prefix].push(num);
}
}
});
return matches;
}

// Generate text for sharing matches found
export function generateMatchesText(matches) {
const albumOrder = ["PANINI","FWC","CC"];
GROUPS.forEach(g=>{ g.teams.forEach(tc=>albumOrder.push(tc)); });
const sortedGroups = Object.keys(matches).sort((a,b)=>albumOrder.indexOf(a)-albumOrder.indexOf(b));
if(sortedGroups.length===0) return "No matches found";
const lines = sortedGroups.map(prefix=>{
const numbers = matches[prefix].sort((a,b)=>typeof a==="string"?a.localeCompare(b):a-b);
const formatted = numbers.map(n=>typeof n==="string"?n:n.toString().padStart(2,"0")).join(", ");
return `${prefix}: ${formatted}`;
});
return "Ey parce! Estas son mis repetidas que vos necesitás:\n\nRegístrate en "+APP_URL+" para que intercambiemos\n\n"+lines.join("\n");
}

// Generate text for missing matches found
export function generateMissingMatchesText(matches) {
const albumOrder = ["PANINI","FWC","CC"];
GROUPS.forEach(g=>{ g.teams.forEach(tc=>albumOrder.push(tc)); });
const sortedGroups = Object.keys(matches).sort((a,b)=>albumOrder.indexOf(a)-albumOrder.indexOf(b));
if(sortedGroups.length===0) return "No matches found";
const lines = sortedGroups.map(prefix=>{
const numbers = matches[prefix].sort((a,b)=>typeof a==="string"?a.localeCompare(b):a-b);
const formatted = numbers.map(n=>typeof n==="string"?n:n.toString().padStart(2,"0")).join(", ");
return `${prefix}: ${formatted}`;
});
return "Ey parce! Estas son las láminas que me faltan y vos tenés repetidas:\n\nRegístrate en "+APP_URL+" para que intercambiemos\n\n"+lines.join("\n");
}
